import "./index.css";

import { ConversationDetails } from "@/components/conversation-details";
import { ChatDetailsPanel } from "@/components/internal-chats/chat-details-panel";
import { NewChatView } from "@/components/internal-chats/new-chat-view";
import { SupportChannelList } from "@/components/internal-chats/support-channel-list";
import { useAbility } from "@/lib/casl/ability-context";
import { useFeatureFlagEnabled } from "@posthog/react";
import { useChannelsList } from "@/lib/hooks/conversations/use-channels-list";
import { useCurrentConversation } from "@/lib/hooks/conversations/use-current-conversation";
import { useDetailsSidebarState } from "@/lib/hooks/conversations/use-details-sidebar-state";
import { useMentorView } from "@/lib/hooks/admin/use-mentor-view";
import {
  InternalOpsChannel,
  useInternalOpsChannel,
} from "@/lib/api/internal-ops-messaging";
import { useCurrentUser } from "@/lib/api/user";
import { INTERNAL_OPS_CUSTOM_TYPES } from "@/lib/constants";
import { PostHogEvents, useTrackEvent } from "@/lib/posthog-events";
import { cn } from "@/lib/utils";
import { PencilSquareIcon } from "@heroicons/react/24/outline";
import { ChannelProvider } from "@sendbird/uikit-react/Channel/context";
import { Moodies } from "@somethings-ui/core/assets";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { Hub } from "../hub/hub";
import { HubStoreProvider, useHubStore } from "../hub/use-hub-store";
import { ChannelDetails } from "../sendbird-chat/channel-details";
import { ChannelHeader } from "./channel-header";
import { ConversationAccordionListing } from "./conversation-accordion-listing";
import { EmptyConversationState } from "./empty-conversation-state";
import { MentorMomentBanner } from "./magic-moment/mentor-moment-banner";
import { MessageInput } from "./message-input";

type ChatSegment = "teens" | "support";

type SegmentTab = { id: ChatSegment; label: string; unreadCount: number };

type SearchPatch = Partial<{
  segment: ChatSegment;
  channel: string | undefined;
  internalChannelId: string | undefined;
}>;

const CONVERSATIONS_ROUTE = "/conversations/" as const;

export function Conversations() {
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as {
    segment?: string;
    channel?: string;
    internalChannelId?: string;
  };
  const requestedSegment: ChatSegment =
    search.segment === "support" ? "support" : "teens";
  const initialChannel =
    typeof search.channel === "string" && search.channel.length > 0
      ? search.channel
      : null;
  const initialInternalChannelId =
    typeof search.internalChannelId === "string" &&
    search.internalChannelId.length > 0
      ? search.internalChannelId
      : null;
  const { data: currentUser } = useCurrentUser();
  const { track } = useTrackEvent();
  const { currentConversation, setCurrentConversation } =
    useCurrentConversation();
  const ability = useAbility();
  const canCreateMessages = ability.can("create", "messages");
  const canReadMessages = ability.can("read", "messages");
  const isMentorOpsSupportEnabled = useFeatureFlagEnabled(
    "enable-mentor-ops-support",
  );

  const systemRoles = currentUser?.systemRoles ?? [];
  const hasMentorRole = systemRoles.includes("mentor");
  const canReadInternalChannels =
    ability.can("read", "internal-channels") && !!isMentorOpsSupportEnabled;
  const mentorView = useMentorView((state) => state.mentorView);
  const canSeeTeenChats = canReadMessages && (hasMentorRole || mentorView);

  const [supportInternalChannelId, setSupportInternalChannelId] = useState<
    string | null
  >(null);
  const [composing, setComposing] = useState(false);

  const showLegacySupervisorSection =
    canSeeTeenChats && hasMentorRole && !isMentorOpsSupportEnabled;
  const teenCustomTypesFilter = useMemo<string[]>(
    () =>
      showLegacySupervisorSection
        ? ["MENTORSHIP", "SUPERVISION"]
        : ["MENTORSHIP"],
    [showLegacySupervisorSection],
  );
  const { channels: teenChannels, loading: teenLoading } = useChannelsList({
    customTypesFilter: teenCustomTypesFilter,
    enabled: canSeeTeenChats,
    enableNotifications: false,
  });
  const { channels: supportChannels, loading: supportLoading } =
    useChannelsList({
      customTypesFilter: [...INTERNAL_OPS_CUSTOM_TYPES],
      enabled: canReadInternalChannels,
      enableNotifications: false,
    });

  const teenUnread = useMemo(
    () => teenChannels.filter((c) => c.unreadMessageCount > 0).length,
    [teenChannels],
  );
  const supportUnread = useMemo(
    () => supportChannels.filter((c) => c.unreadMessageCount > 0).length,
    [supportChannels],
  );

  const availableSegments = useMemo<SegmentTab[]>(() => {
    const tabs: SegmentTab[] = [];
    if (canSeeTeenChats) {
      tabs.push({
        id: "teens",
        label: "Teens",
        unreadCount: teenUnread,
      });
    }
    if (canReadInternalChannels) {
      tabs.push({
        id: "support",
        label: "Support",
        unreadCount: supportUnread,
      });
    }
    return tabs;
  }, [canSeeTeenChats, canReadInternalChannels, teenUnread, supportUnread]);

  const segment: ChatSegment = availableSegments.some(
    (t) => t.id === requestedSegment,
  )
    ? requestedSegment
    : (availableSegments[0]?.id ?? "support");

  useEffect(() => {
    setCurrentConversation(initialChannel);
    setSupportInternalChannelId(
      segment === "support" ? initialInternalChannelId : null,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!currentUser) return;
    track(PostHogEvents.OPS_CHAT_DASHBOARD_VIEW, {
      role: currentUser.role,
      default_segment: segment,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.id]);

  const replaceSearch = useCallback(
    (patch: SearchPatch) =>
      navigate({
        from: CONVERSATIONS_ROUTE,
        search: (prev) => ({ ...prev, ...patch }),
        replace: true,
      }),
    [navigate],
  );

  const handleSegmentChange = (next: ChatSegment) => {
    if (next === segment) return;
    track(PostHogEvents.OPS_CHAT_SEGMENT_TOGGLE, { from: segment, to: next });
    replaceSearch({
      segment: next,
      channel: undefined,
      internalChannelId: undefined,
    });
    setCurrentConversation(null);
    setSupportInternalChannelId(null);
    setComposing(false);
  };

  const handleOpenNewChat = () => {
    track(PostHogEvents.OPS_CHAT_NEW_CHAT_OPEN, {
      entry: segment === "teens" ? "teens_segment" : "support_segment",
    });
    setCurrentConversation(null);
    setSupportInternalChannelId(null);
    setComposing(true);
    replaceSearch({ channel: undefined, internalChannelId: undefined });
  };

  const handleSelectTeenChannel = useCallback(
    (channelUrl: string | null) => {
      setCurrentConversation(channelUrl);
      setSupportInternalChannelId(null);
      setComposing(false);
      replaceSearch({
        segment: "teens",
        channel: channelUrl ?? undefined,
        internalChannelId: undefined,
      });
    },
    [replaceSearch, setCurrentConversation],
  );

  const handleSelectSupportChannel = useCallback(
    (payload: { sdkUrl: string; internalChannelId: string | null }) => {
      setCurrentConversation(payload.sdkUrl);
      setSupportInternalChannelId(payload.internalChannelId);
      setComposing(false);
      replaceSearch({
        segment: "support",
        channel: payload.sdkUrl,
        internalChannelId: payload.internalChannelId ?? undefined,
      });
    },
    [replaceSearch, setCurrentConversation],
  );

  return (
    <div className="flex h-screen">
      <div className="flex h-full w-48 flex-shrink-0 flex-col overflow-hidden border-r border-zinc-300 sm:w-[200px] lg:w-[400px]">
        <div className="flex items-center justify-end gap-2.5 border-b border-zinc-200 bg-white p-4">
          <SegmentedControl
            segment={segment}
            onSegmentChange={handleSegmentChange}
            availableSegments={availableSegments}
          />
          {segment === "support" && canCreateMessages && (
            <button
              type="button"
              onClick={handleOpenNewChat}
              aria-label="New chat"
              className="flex size-10 shrink-0 items-center justify-center rounded-md border border-zinc-200 bg-white text-zinc-900 transition-colors hover:bg-zinc-50"
            >
              <PencilSquareIcon className="size-5" />
            </button>
          )}
        </div>

        <div className="flex-1 overflow-y-auto">
          {segment === "teens" ? (
            <ConversationAccordionListing
              channels={teenChannels}
              channelsLoading={teenLoading}
              currentChannelUrl={currentConversation}
              setCurrentChannelUrl={handleSelectTeenChannel}
              shouldEverShowCheckinBadge={true}
              showSupervisorSection={showLegacySupervisorSection}
            />
          ) : (
            <SupportChannelList
              channels={supportChannels}
              loading={supportLoading}
              currentChannelUrl={currentConversation}
              onSelectChannel={handleSelectSupportChannel}
            />
          )}
        </div>
      </div>

      <ActiveConversationPane
        composing={composing}
        segment={segment}
        currentConversation={currentConversation}
        supportInternalChannelId={supportInternalChannelId}
        canCreateMessages={canCreateMessages}
        onCancelCompose={() => setComposing(false)}
        onSupportChannelReady={handleSelectSupportChannel}
      />
    </div>
  );
}

const ActiveConversationPane = memo(function ActiveConversationPane({
  composing,
  segment,
  currentConversation,
  supportInternalChannelId,
  canCreateMessages,
  onCancelCompose,
  onSupportChannelReady,
}: {
  composing: boolean;
  segment: ChatSegment;
  currentConversation: string | null;
  supportInternalChannelId: string | null;
  canCreateMessages: boolean;
  onCancelCompose: () => void;
  onSupportChannelReady: (payload: {
    sdkUrl: string;
    internalChannelId: string | null;
  }) => void;
}) {
  const { data: opsInternalChannel } = useInternalOpsChannel(
    segment === "support" ? supportInternalChannelId : null,
  );

  if (composing) {
    return (
      <NewChatView
        onCancel={onCancelCompose}
        onChannelReady={onSupportChannelReady}
      />
    );
  }

  if (!currentConversation) {
    return (
      <div className="flex flex-1">
        <EmptyConversationState segment={segment} />
      </div>
    );
  }

  return (
    <ChatThread
      channelUrl={currentConversation}
      canCreateMessages={canCreateMessages}
      opsChannel={
        segment === "support" ? (opsInternalChannel ?? null) : undefined
      }
    />
  );
});

function SegmentedControl({
  segment,
  onSegmentChange,
  availableSegments,
}: {
  segment: ChatSegment;
  onSegmentChange: (next: ChatSegment) => void;
  availableSegments: SegmentTab[];
}) {
  if (availableSegments.length < 2) {
    const only = availableSegments[0];
    return (
      <div className="flex h-10 flex-1 items-center">
        <span className="text-base font-semibold text-zinc-900">
          {only?.label ?? "Chats"}
        </span>
      </div>
    );
  }

  return (
    <div className="flex h-10 flex-1 items-center justify-center gap-2 rounded-md border border-zinc-200 bg-white p-[3px]">
      {availableSegments.map((tab) => {
        const isActive = segment === tab.id;
        const showBadge = !isActive && tab.unreadCount > 0;
        return (
          <button
            key={tab.id}
            type="button"
            onClick={() => onSegmentChange(tab.id)}
            className={cn(
              "flex h-full min-w-0 flex-1 items-center justify-center gap-1.5 rounded-sm px-2 text-sm leading-5 transition-colors",
              isActive
                ? "bg-zinc-200 text-zinc-900"
                : "text-zinc-500 hover:text-zinc-700",
            )}
          >
            <span>{tab.label}</span>
            {showBadge && (
              <span className="flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-[5px] text-[10px] font-medium leading-none text-white">
                {tab.unreadCount > 99 ? "99+" : tab.unreadCount}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

interface ChatThreadProps {
  channelUrl: string;
  canCreateMessages: boolean;
  /**
   * Presence of this prop switches the thread into ops mode. `null` means the
   * ops channel is still loading; a value means it's resolved.
   */
  opsChannel?: InternalOpsChannel | null;
  /**
   * Optional override for the channel header slot. When omitted, the standard
   * ChannelHeader is rendered. NewChatView passes null to suppress it (the
   * To: bar serves as the header).
   */
  channelHeader?: React.ReactElement | null;
  /**
   * Called after a message is successfully sent through this thread. Used by
   * NewChatView to exit compose mode after the first message lands.
   */
  onAfterSend?: () => void;
}

/**
 * Unified chat thread used by both teen and ops chats. Branches:
 *   - Right sidebar: ChatDetailsPanel (ops) vs ConversationDetails sidebar (teen).
 *   - MentorMomentBanner is teen-only.
 *   - MessageInput receives `opsContext` in ops mode, which switches
 *     persistence to `internal_ops_messages`.
 *
 * The Hub provider/handler wraps unconditionally; they're inert for ops since
 * pendingEntity is only set by teen-specific flows.
 */
export const ChatThread = memo(function ChatThread({
  channelUrl,
  canCreateMessages,
  opsChannel,
  channelHeader,
  onAfterSend,
}: ChatThreadProps) {
  const isOpsMode = opsChannel !== undefined;

  const opsContext = useMemo(
    () =>
      isOpsMode
        ? { internalChannelId: opsChannel?.id ?? null, onAfterSend }
        : undefined,
    [isOpsMode, opsChannel?.id, onAfterSend],
  );

  const opsTitle = useMemo(() => {
    if (!opsChannel) return null;
    if (opsChannel.kind === "direct") return null;
    return opsChannel.channel_display_name ?? null;
  }, [opsChannel]);

  const opsAvatar = useMemo(() => {
    if (opsChannel?.kind !== "cohort") return undefined;
    return (
      <img
        src={Moodies.happy}
        alt="Cohort"
        className="size-10 shrink-0 object-contain"
      />
    );
  }, [opsChannel?.kind]);

  const header =
    channelHeader === undefined ? (
      <ChannelHeader
        isSupervisorChannel={isOpsMode}
        titleOverride={opsTitle}
        avatarOverride={opsAvatar}
      />
    ) : (
      channelHeader
    );

  return (
    <HubStoreProvider key={channelUrl}>
      <PendingEntityHandler />
      <ChannelProvider channelUrl={channelUrl} replyType="QUOTE_REPLY">
        <div className="flex flex-1">
          <div className="max-w-96 flex-1 md:max-w-[450px] lg:max-w-full">
            <ChannelDetails
              currentChannelUrl={channelUrl}
              channelHeader={header ?? <></>}
              messageInput={
                canCreateMessages ? (
                  <div>
                    {!isOpsMode && (
                      <MentorMomentBanner currentConversation={channelUrl} />
                    )}
                    <MessageInput
                      key={channelUrl}
                      shouldShowAiConversation={!isOpsMode}
                      shouldShowVideoCallButton={false}
                      opsContext={opsContext}
                    />
                  </div>
                ) : (
                  <></>
                )
              }
            />
          </div>
          {isOpsMode ? (
            <ChatDetailsPanel internalChannel={opsChannel ?? null} />
          ) : (
            <DetailsSidebarPanel />
          )}
        </div>
      </ChannelProvider>
    </HubStoreProvider>
  );
});

const PendingEntityHandler = () => {
  const pendingEntity = useCurrentConversation((state) => state.pendingEntity);
  const setPendingEntity = useCurrentConversation(
    (state) => state.setPendingEntity,
  );
  const open = useHubStore((state) => state.open);
  const navigateToView = useHubStore((state) => state.navigateToView);

  useEffect(() => {
    if (!pendingEntity) return;

    open();
    if (pendingEntity.type === "NOTE") {
      navigateToView("view-note", pendingEntity.id);
    } else if (pendingEntity.type === "MOMENT") {
      navigateToView("view-moment", pendingEntity.id);
    } else if (pendingEntity.type === "SWAP") {
      navigateToView("view-swap", pendingEntity.id);
    }
    setPendingEntity(null);
  }, [pendingEntity, open, navigateToView, setPendingEntity]);

  return null;
};

const DetailsSidebarPanel = () => {
  const { isCollapsed, onToggleCollapse } = useDetailsSidebarState();
  const isHubOpen = useHubStore((state: { isOpen: boolean }) => state.isOpen);

  return (
    <div
      className={cn(
        "border-l transition-all duration-300",
        isHubOpen
          ? "w-full lg:w-[500px] xl:w-[600px]"
          : isCollapsed
            ? "w-[50px]"
            : "max-w-96 lg:w-[450px]",
      )}
    >
      {isHubOpen ? (
        <Hub />
      ) : (
        <ConversationDetails
          isCollapsed={isCollapsed}
          onToggleCollapse={onToggleCollapse}
        />
      )}
    </div>
  );
};

export type { ChatSegment };
